import { useId, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { buzz } from '@/native/haptics'
import { cn } from '@/lib/utils'

export interface SegmentOption<T extends string> {
  value: T
  label: string
  /** Shown under the control for the selected option. */
  description?: string
  /** Haptic taps when chosen (iOS app). */
  buzzes?: number
}

/**
 * Full-width, iOS-style segmented control. The selected segment's "glass" thumb springs
 * between options, and stretches slightly as it lands, like iOS's liquid glass controls.
 *
 * Built to feel instant: a tap moves the thumb, updates the description, and buzzes straight
 * away from the control's own state. `onChange` (which may save settings and re-price the
 * letter) runs only after the animation's first frames are on screen. Honors Reduce Motion.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  className,
}: {
  label: string
  options: SegmentOption<T>[]
  value: T | undefined
  onChange: (value: T) => void
  className?: string
}) {
  const groupId = useId()
  const reduceMotion = useReducedMotion()
  // What the control shows. It follows `value`, but moves ahead of it on a tap.
  const [shown, setShown] = useState(value)
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    setShown(value)
  }
  // The landing stretch plays only after a tap, not on first render.
  const [touched, setTouched] = useState(false)

  function select(option: SegmentOption<T>) {
    buzz(option.buzzes ?? 1)
    if (option.value === shown) return
    setTouched(true)
    setShown(option.value)
    // Two frames: the thumb's first animated frame paints before any heavier work starts.
    requestAnimationFrame(() => requestAnimationFrame(() => onChange(option.value)))
  }

  const slide = reduceMotion ? { duration: 0 } : { type: 'spring' as const, stiffness: 620, damping: 40, mass: 0.7 }
  const description = options.find((o) => o.value === shown)?.description

  return (
    <div className="space-y-2">
      <div
        role="radiogroup"
        aria-label={label}
        className={cn(
          'relative flex w-full p-[3px] rounded-full',
          'bg-foreground/[0.06] ring-1 ring-inset ring-foreground/[0.06] backdrop-blur-xl',
          className,
        )}
      >
        {options.map((o) => {
          const selected = shown === o.value
          return (
            <motion.button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => select(o)}
              whileTap={reduceMotion ? undefined : { scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 700, damping: 30 }}
              className={cn(
                // Natural width plus an equal share of the spare room, so long labels fit on small phones.
                'relative flex-auto min-w-0 h-9 px-1 rounded-full text-[13px] transition-colors duration-150',
                'touch-manipulation select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected ? 'text-foreground font-semibold' : 'text-muted-foreground',
              )}
            >
              {selected && (
                // Outer layer moves between segments; inner layer does the landing stretch.
                <motion.span layoutId={`${groupId}-thumb`} transition={slide} className="absolute inset-0" aria-hidden>
                  <motion.span
                    key={o.value}
                    initial={false}
                    animate={touched && !reduceMotion ? { scaleX: [1, 1.08, 1], scaleY: [1, 0.9, 1] } : undefined}
                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                    className={cn(
                      'absolute inset-0 rounded-full',
                      // Glass: translucent fill, blur and saturation, a hairline edge, soft
                      // shadow, and a top sheen.
                      'bg-white/75 dark:bg-white/[0.13] backdrop-blur-md backdrop-saturate-150',
                      'ring-1 ring-black/[0.05] dark:ring-white/[0.12]',
                      'shadow-[0_1px_2px_rgb(0_0_0/0.06),0_6px_16px_-4px_rgb(0_0_0/0.12),inset_0_1px_0_rgb(255_255_255/0.85)]',
                      'dark:shadow-[0_1px_2px_rgb(0_0_0/0.4),0_6px_16px_-4px_rgb(0_0_0/0.5),inset_0_1px_0_rgb(255_255_255/0.16)]',
                      'before:absolute before:inset-x-2 before:top-0 before:h-1/2 before:rounded-full',
                      'before:bg-gradient-to-b before:from-white/50 before:to-transparent dark:before:from-white/10',
                    )}
                  />
                </motion.span>
              )}
              <span className="relative z-10 block truncate">{o.label}</span>
            </motion.button>
          )
        })}
      </div>
      {description && <p className="text-[11px] text-muted-foreground leading-snug px-1">{description}</p>}
    </div>
  )
}
